/**
 * TMUsersPage — admin-only user management: approve, reject, change role, deactivate
 */
import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, ShieldCheck, ShieldOff, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTMAuth } from '@/contexts/TMAuthContext';
import {
  getAllTMUsers, approveTMUser, rejectTMUser,
  deactivateTMUser, updateTMUserRole,
} from '@/services/tmService';
import type { TMUser, TMUserStatus, TMUserRole } from '@/types/tm.types';
import { cn } from '@/utils/cn';

const STATUS_BADGE: Record<TMUserStatus, string> = {
  pending:     'bg-amber-100 text-amber-700',
  approved:    'bg-emerald-100 text-emerald-700',
  rejected:    'bg-red-100 text-red-600',
  deactivated: 'bg-gray-100 text-gray-500',
};
const STATUS_LABEL: Record<TMUserStatus, string> = {
  pending:     'Pending',
  approved:    'Active',
  rejected:    'Rejected',
  deactivated: 'Deactivated',
};

type Tab = 'pending' | 'approved' | 'all';

export default function TMUsersPage() {
  const { tmUser } = useTMAuth();
  const [users,   setUsers]   = useState<TMUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState<Tab>('pending');
  const [busy,    setBusy]    = useState<string | null>(null); // uid of user being acted on

  const load = async () => {
    setLoading(true);
    const all = await getAllTMUsers();
    setUsers(all);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = users.filter(u => {
    if (tab === 'pending')  return u.status === 'pending';
    if (tab === 'approved') return u.status === 'approved';
    return true;
  });

  const pendingCount = users.filter(u => u.status === 'pending').length;

  const handleApprove = async (u: TMUser, role: TMUserRole = 'member') => {
    if (!tmUser) return;
    setBusy(u.uid);
    await approveTMUser(u.uid, tmUser.uid, role);
    toast.success(`${u.name} approved as ${role}`);
    await load();
    setBusy(null);
  };

  const handleReject = async (u: TMUser) => {
    if (!confirm(`Reject ${u.name}?`)) return;
    setBusy(u.uid);
    await rejectTMUser(u.uid);
    toast.success(`${u.name} rejected`);
    await load();
    setBusy(null);
  };

  const handleDeactivate = async (u: TMUser) => {
    if (!confirm(`Deactivate ${u.name}?`)) return;
    setBusy(u.uid);
    await deactivateTMUser(u.uid);
    toast.success(`${u.name} deactivated`);
    await load();
    setBusy(null);
  };

  const handleRoleChange = async (u: TMUser, role: TMUserRole) => {
    setBusy(u.uid);
    await updateTMUserRole(u.uid, role);
    toast.success(`${u.name} is now ${role}`);
    await load();
    setBusy(null);
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-gray-900">Members</h1>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
        {([
          { key: 'pending',  label: 'Pending' },
          { key: 'approved', label: 'Active' },
          { key: 'all',      label: 'All' },
        ] as { key: Tab; label: string }[]).map(({ key, label }) => (
          <button key={key} onClick={() => setTab(key)}
            className={cn('relative flex-1 py-2 rounded-lg text-xs font-semibold transition-all',
              tab === key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500')}>
            {label}
            {key === 'pending' && pendingCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 rounded-2xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-14 text-gray-400 text-sm bg-white rounded-2xl border border-gray-100">
          No {tab === 'pending' ? 'pending' : tab === 'approved' ? 'active' : ''} members.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(u => (
            <div key={u.uid}
              className={cn('bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3', busy === u.uid && 'opacity-60')}>

              {/* Info row */}
              <div className="flex items-start gap-3">
                {/* Avatar circle */}
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-indigo-600">{u.name.charAt(0).toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{u.name}</p>
                  <p className="text-xs text-gray-400 truncate">{u.email}</p>
                  {u.phone && <p className="text-xs text-gray-400">{u.phone}</p>}
                </div>
                <span className={cn('shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full', STATUS_BADGE[u.status])}>
                  {STATUS_LABEL[u.status]}
                </span>
              </div>

              {/* Role + joined */}
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span className={cn('capitalize px-2 py-0.5 rounded-full font-medium',
                  u.role === 'admin' ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-500')}>
                  {u.role}
                </span>
                {u.createdAt && (
                  <span>Joined {u.createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-wrap">
                {u.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleApprove(u, 'member')}
                      disabled={!!busy}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600 text-white text-xs font-semibold active:scale-95 transition-all disabled:opacity-60">
                      <CheckCircle className="h-3.5 w-3.5" />
                      Approve as Member
                    </button>
                    <button
                      onClick={() => handleApprove(u, 'admin')}
                      disabled={!!busy}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-purple-600 text-white text-xs font-semibold active:scale-95 transition-all disabled:opacity-60">
                      <ShieldCheck className="h-3.5 w-3.5" />
                      Approve as Admin
                    </button>
                    <button
                      onClick={() => handleReject(u)}
                      disabled={!!busy}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-red-200 text-red-500 text-xs font-semibold active:scale-95 transition-all disabled:opacity-60">
                      <XCircle className="h-3.5 w-3.5" />
                      Reject
                    </button>
                  </>
                )}

                {u.status === 'approved' && (
                  <>
                    {/* Role toggle */}
                    <div className="relative">
                      <select
                        value={u.role}
                        disabled={!!busy}
                        onChange={e => handleRoleChange(u, e.target.value as TMUserRole)}
                        className="appearance-none pl-3 pr-7 py-2 rounded-xl border border-gray-200 text-xs font-medium text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 cursor-pointer">
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                    </div>
                    <button
                      onClick={() => handleDeactivate(u)}
                      disabled={!!busy}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-gray-500 text-xs font-semibold active:scale-95 transition-all disabled:opacity-60">
                      <ShieldOff className="h-3.5 w-3.5" />
                      Deactivate
                    </button>
                  </>
                )}

                {(u.status === 'rejected' || u.status === 'deactivated') && (
                  <button
                    onClick={() => handleApprove(u, 'member')}
                    disabled={!!busy}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-600 text-white text-xs font-semibold active:scale-95 transition-all disabled:opacity-60">
                    <CheckCircle className="h-3.5 w-3.5" />
                    Re-approve
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
